// SalesPurchaseView.js — "매출매입" 전체화면 오버레이. 기존 SalesByVendorView(업체별 매출)를 확장해
// 매출(이카운트/쿠팡 매출) + 매입(구매계획 이력) + 현금흐름(관리) 3개 탭으로 재구성했다.
// 현금흐름 탭은 실제 통장 잔액·고정비를 관리자가 직접 입력해 반영하는 "관리" 기능을 포함한다.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TrendingUp, TrendingDown, Wallet, X, RefreshCw, FileSpreadsheet, ChevronDown, ChevronRight, Upload, Plus, Trash2, Boxes } from "lucide-react";
import XLSXStyle from "xlsx-js-style";
import Chart from "chart.js/auto";
import { fetchSalesRecords, summarizeByVendor, parseSalesExcelRows, saveSalesRecords, replaceSalesRecords, rangeBounds } from "./ecountSales";
import { fetchPurchaseRecords, parsePurchaseExcelRows, savePurchaseRecords } from "./purchaseTaxInvoice";
import { fetchProcurementPlans, summarizeByVendorPurchase, monthlyPaymentForecast, isUSD, paymentSchedule } from "./procurementPlans";
import { buildMonthlyCashflow, ymd, addDays } from "./weeklyReportCalc";
import { buildMonthlyCogs } from "./inventoryCogs";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "./firebase";

function ChartBox({ config, height = 240 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const c = new Chart(ref.current, config);
    return () => c.destroy();
  }, [config]);
  return <div style={{ height }} className="relative"><canvas ref={ref} /></div>;
}

const won = (n) => (n == null ? "—" : "₩" + Math.round(Number(n)).toLocaleString("ko-KR"));
const nf = (n) => (Number(n) || 0).toLocaleString("ko-KR");
const pct = (n, t) => (t > 0 ? ((n / t) * 100).toFixed(1) + "%" : "—");

// 거래처명 정규화 — 실제 매입(세금계산서)과 구매계획의 표기 차이((주)·㈜·주식회사·공백·대소문자)를 흡수해 동일 업체를 판별한다.
const normVendorName = (s) => String(s || "").toLowerCase().replace(/\(주\)|㈜|주식회사|유한회사|\s+/g, "");

const RANGES = [
  { d: 30, t: "최근 30일" },
  { d: 90, t: "최근 90일" },
  { d: "ytd", t: "올해" },
  { d: 0, t: "전체" },
];

// 매출 탭 전용 기간 목록 — 분기·반기는 모두 '올해' 기준이다(ecountSales.rangeBounds).
// 매입 탭은 위 RANGES를 그대로 쓴다. 매입에도 붙이려면 아래 목록으로 바꿔 끼우기만 하면 된다.
const SALES_RANGES = [
  { d: 30, t: "최근 30일" },
  { d: 90, t: "최근 90일" },
  { d: "q1", t: "1분기" },
  { d: "q2", t: "2분기" },
  { d: "q3", t: "3분기" },
  { d: "q4", t: "4분기" },
  { d: "h1", t: "상반기" },
  { d: "h2", t: "하반기" },
  { d: "ytd", t: "올해" },
  { d: 0, t: "전체" },
];

// 브랜디노 비서 매출은 Cloud Function으로 "최근 N일"만 받아올 수 있다.
// 분기·반기 필터가 올해 기준이므로 올해 1월 1일까지는 덮이도록 조회 기간을 잡는다.
const BRANDINO_FETCH_DAYS = (() => {
  const now = new Date();
  const jan1 = new Date(now.getFullYear(), 0, 1);
  return Math.max(200, Math.round((now - jan1) / 86400000) + 1);
})();

// 브랜디노 비서(외부 도구)에서 자동으로 가져오는 쿠팡 채널 — 이카운트 판매현황 엑셀에는 안 잡힌다.
// label에 "쿠팡"을 포함시켜 현금흐름 계산의 기존 쿠팡 지급기준(70%@+60일·30%@+90일)이 그대로 적용되게 한다.
const BRANDINO_CHANNELS = [
  { key: "rocketGrowth", label: "쿠팡 로켓그로스" },
  { key: "jet", label: "쿠팡 제트배송" },
];

const SummaryCard = ({ label, value, sub, tone }) => (
  <div className={"rounded-xl p-4 shadow-sm text-white " + (tone === "red" ? "bg-red-600" : tone === "amber" ? "bg-amber-600" : "bg-slate-900")}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-white/70 mb-1.5">{label}</div>
    <div className="text-xl font-semibold tabular-nums leading-tight">{value}</div>
    {sub && <div className="text-[11px] text-white/70 mt-0.5">{sub}</div>}
  </div>
);

const TabBtn = ({ active, onClick, icon, children }) => (
  <button onClick={onClick} className={"flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all " + (active ? "bg-slate-900 text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:text-slate-800")}>
    {icon}{children}
  </button>
);

// 2026-07-25: '실제 통장 잔액 입력'·'고정비 관리' UI는 사용자 요청으로 화면에서 제외했다.
// 계산 로직(actualBalance/fixedCosts)은 weeklyReportCalc.js에 그대로 살아있고 App.js의 상태·저장도
// 유지되므로, 나중에 다시 노출하고 싶으면 여기서 props만 도로 넘겨주면 된다.
export default function SalesPurchaseView({ onClose, isAdmin, otherInflows = [], addOtherInflow, removeOtherInflow, valueHistory = {}, brandinoSales = [], removeBrandinoSale }) {
  const [innerTab, setInnerTab] = useState("sales"); // sales | purchase | cogs | cashflow

  // ===== 매출 (기존 SalesByVendorView 로직 그대로) =====
  const [records, setRecords] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState("ytd");
  const [open, setOpen] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [replaceMode, setReplaceMode] = useState(false); // 체크 시: 기존 매출 자료를 전부 지우고 이번 파일로만 교체
  const fileRef = useRef(null);

  const load = useCallback(async (d) => {
    setLoading(true);
    try { setRecords(await fetchSalesRecords(d)); setErr(""); }
    catch (e) { setErr(e.message || String(e)); setRecords((prev) => prev || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(days); }, [load, days]);

  const excelVendors = useMemo(() => summarizeByVendor(records), [records]);
  const excelTotal = useMemo(() => excelVendors.reduce((s, v) => s + v.total, 0), [excelVendors]);

  // ===== 브랜디노 비서 매출(쿠팡 로켓그로스·제트배송) =====
  // 1순위: 브랜디노 비서 API 자동 조회(fetchBrandinoSales Cloud Function, 서버 대 서버 — CLAUDE.md §6.2).
  // 2순위: 화면에서 직접 입력한 값 — 같은 채널·날짜가 있으면 수동 입력이 자동 조회값을 덮어쓴다(보정용).
  const [brandinoApi, setBrandinoApi] = useState(null); // API에서 불러온 [{date, vendorName, amount}]
  const [brandinoApiErr, setBrandinoApiErr] = useState("");
  const [brandinoApiLoading, setBrandinoApiLoading] = useState(false);
  const loadBrandinoApi = useCallback(async () => {
    setBrandinoApiLoading(true);
    try {
      const fns = getFunctions(app, "asia-northeast3");
      const res = await httpsCallable(fns, "fetchBrandinoSales", { timeout: 60000 })({ days: BRANDINO_FETCH_DAYS });
      setBrandinoApi(res.data?.records || []);
      setBrandinoApiErr("");
    } catch (e) {
      setBrandinoApiErr(e.message || String(e));
      setBrandinoApi((prev) => prev || []);
    } finally {
      setBrandinoApiLoading(false);
    }
  }, []);
  useEffect(() => { loadBrandinoApi(); }, [loadBrandinoApi]);

  // summarizeByVendor와 같은 모양(vendor/total/daily/dayCount/lastDate)으로 맞춰 이카운트 매출과 동일하게 다룬다.
  const brandinoRecords = useMemo(() => {
    const byKey = {};
    (brandinoApi || [])
      .filter((r) => r.date && Number(r.amount))
      .forEach((r) => {
        const ch = BRANDINO_CHANNELS.find((c) => c.label === r.vendorName);
        const chKey = ch ? ch.key : r.vendorName;
        const key = `${chKey}_${r.date}`; // byKey 중복 제거용(채널·날짜 단위) — vendorCode에는 안 씀
        // vendorCode는 채널당 하나로 고정해야 summarizeByVendor가 날짜별로 흩어지지 않고 한 행으로 합쳐진다.
        byKey[key] = { date: r.date, vendorCode: `brandino_${chKey}`, vendorName: r.vendorName, amount: Number(r.amount) || 0, supplyAmount: Number(r.amount) || 0, qty: 0 };
      });
    (brandinoSales || [])
      .filter((b) => b.date && Number(b.amount))
      .forEach((b) => {
        const ch = BRANDINO_CHANNELS.find((c) => c.key === b.channel);
        const key = `${b.channel}_${b.date}`;
        byKey[key] = { date: b.date, vendorCode: `brandino_${b.channel}`, vendorName: ch ? ch.label : "쿠팡(브랜디노)", amount: Number(b.amount) || 0, supplyAmount: Number(b.amount) || 0, qty: 0 };
      });
    // ⚠️ 이카운트 매출은 Firestore 조회 단계에서 기간이 걸리지만 브랜디노 매출은 전량 들어온다.
    //    여기서 같은 기간으로 걸러주지 않으면 "1분기"를 골라도 쿠팡 매출은 전 기간이 더해져 합계가 부풀려진다.
    const { from, to } = rangeBounds(days);
    return Object.values(byKey).filter((r) => (!from || r.date >= from) && (!to || r.date <= to));
  }, [brandinoApi, brandinoSales, days]);
  const brandinoVendors = useMemo(() => summarizeByVendor(brandinoRecords), [brandinoRecords]);
  const brandinoTotal = useMemo(() => brandinoVendors.reduce((s, v) => s + v.total, 0), [brandinoVendors]);

  // 화면 표시용 매출 = 엑셀 업로드(이카운트) 매출 + 브랜디노 비서 매출(로켓그로스·제트배송)을 합산한 값.
  // 아래 vendors/total을 쓰는 요약카드·차트·업체별 표는 모두 합산 기준으로 자동 반영된다.
  // 로켓그로스·제트배송은 각각 채널 단위로 이미 합산돼 있으므로(brandinoRecords의 vendorCode 고정),
  // 다른 거래처들과 똑같이 매출액 내림차순으로만 세우면 된다.
  const vendors = useMemo(
    () => [...excelVendors, ...brandinoVendors].sort((a, b) => b.total - a.total),
    [excelVendors, brandinoVendors],
  );
  const total = useMemo(() => excelTotal + brandinoTotal, [excelTotal, brandinoTotal]);

  const dateCount = useMemo(() => {
    const s = new Set((records || []).map((r) => r.date));
    (brandinoRecords || []).forEach((r) => s.add(r.date));
    return s.size;
  }, [records, brandinoRecords]);
  const monthlyTotals = useMemo(() => {
    const m = {};
    (records || []).forEach((r) => { const k = r.date.slice(0, 7); m[k] = (m[k] || 0) + r.amount; });
    (brandinoRecords || []).forEach((r) => { const k = r.date.slice(0, 7); m[k] = (m[k] || 0) + r.amount; });
    return Object.entries(m).sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [records, brandinoRecords]);
  const mom = useMemo(() => {
    if (monthlyTotals.length < 2) return null;
    const [pm, pv] = monthlyTotals[monthlyTotals.length - 2];
    const [cm, cv] = monthlyTotals[monthlyTotals.length - 1];
    return pv ? { month: cm, prev: pm, pct: ((cv - pv) / Math.abs(pv)) * 100 } : null;
  }, [monthlyTotals]);
  const monthlyConfig = useMemo(() => ({
    type: "bar",
    data: { labels: monthlyTotals.map(([m]) => m), datasets: [{ data: monthlyTotals.map(([, v]) => v), backgroundColor: "#1e293b", borderRadius: 5, maxBarThickness: 48 }] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => " " + won(c.raw) } } }, scales: { y: { ticks: { callback: (v) => won(v) } } } },
  }), [monthlyTotals]);
  const topVendorConfig = useMemo(() => {
    const top = vendors.slice(0, 10);
    return {
      type: "bar",
      data: { labels: top.map((v) => (v.vendor.length > 12 ? v.vendor.slice(0, 12) + "…" : v.vendor)), datasets: [{ data: top.map((v) => v.total), backgroundColor: top.map((_, i) => (i < 3 ? "#4f46e5" : "#94a3b8")), borderRadius: 4 }] },
      options: { indexAxis: "y", responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => " " + won(c.raw) + ` (${pct(c.raw, total)})` } } }, scales: { x: { ticks: { callback: (v) => won(v), maxTicksLimit: 6 } } } },
    };
  }, [vendors, total]);

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    if (replaceMode && !window.confirm("전체 교체를 선택하셨습니다. 기존 매출 자료를 모두 지우고 이번 파일 내용으로만 다시 채웁니다. 계속할까요?")) return;
    setUploading(true); setUploadMsg(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSXStyle.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSXStyle.utils.sheet_to_json(ws, { header: 1, defval: "", raw: true });
      const { error, records } = parseSalesExcelRows(rows);
      if (error) { setUploadMsg({ ok: false, text: error }); return; }
      const r = replaceMode ? await replaceSalesRecords(records) : await saveSalesRecords(records);
      setUploadMsg({
        ok: true,
        text: (replaceMode
          ? `전체 교체 완료 · 기존 ${nf(r.deleted)}건 삭제 → ${r.from} ~ ${r.to} · 업체 ${r.vendors}개사 · ${r.saved}건 재저장`
          : `${r.from} ~ ${r.to} · 업체 ${r.vendors}개사 · ${r.saved}건 저장 완료`)
          + " · 판매현황은 업로드 금액 그대로(부가세 포함) 반영",
      });
      await load(days);
    } catch (err) { setUploadMsg({ ok: false, text: err.message || String(err) }); }
    finally { setUploading(false); }
  };

  // ===== 매입 (구매계획 이력 — 발주앱 기준, 예정 금액) =====
  const [plans, setPlans] = useState(null);
  const [plansErr, setPlansErr] = useState("");
  const [plansLoading, setPlansLoading] = useState(false);
  const [purchaseOpen, setPurchaseOpen] = useState({});
  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try { setPlans(await fetchProcurementPlans()); setPlansErr(""); }
    catch (e) { setPlansErr(e.message || String(e)); setPlans((prev) => prev || []); }
    finally { setPlansLoading(false); }
  }, []);
  useEffect(() => { loadPlans(); }, [loadPlans]);

  const purchaseVendorsAll = useMemo(() => summarizeByVendorPurchase(plans), [plans]);

  // ===== 매입 (매입세금계산서 업로드 — 실제 발생 금액) =====
  const [piRecords, setPiRecords] = useState(null);
  const [piErr, setPiErr] = useState("");
  const [piLoading, setPiLoading] = useState(false);
  const [piDays, setPiDays] = useState("ytd");
  const [piOpen, setPiOpen] = useState({});
  const [piUploading, setPiUploading] = useState(false);
  const [piUploadMsg, setPiUploadMsg] = useState(null);
  const piFileRef = useRef(null);

  const loadPi = useCallback(async (d) => {
    setPiLoading(true);
    try { setPiRecords(await fetchPurchaseRecords(d)); setPiErr(""); }
    catch (e) { setPiErr(e.message || String(e)); setPiRecords((prev) => prev || []); }
    finally { setPiLoading(false); }
  }, []);
  useEffect(() => { loadPi(piDays); }, [loadPi, piDays]);

  const piVendors = useMemo(() => summarizeByVendor(piRecords), [piRecords]);
  const piTotal = useMemo(() => piVendors.reduce((s, v) => s + v.total, 0), [piVendors]);

  // 상단 '실제 매입(매입세금계산서)'에 동일한 거래처가 있으면 구매계획 기준 매입에서 제외한다(이중 집계 방지).
  // 합계(purchaseTotal)·요약카드·엑셀 다운로드 모두 제외 후 금액으로 계산된다.
  const piVendorKeys = useMemo(() => new Set(piVendors.map((v) => normVendorName(v.vendor))), [piVendors]);
  const purchaseVendors = useMemo(() => purchaseVendorsAll.filter((v) => !piVendorKeys.has(normVendorName(v.vendor))), [purchaseVendorsAll, piVendorKeys]);
  const purchaseExcluded = useMemo(() => purchaseVendorsAll.filter((v) => piVendorKeys.has(normVendorName(v.vendor))), [purchaseVendorsAll, piVendorKeys]);
  const purchaseExcludedTotal = useMemo(() => purchaseExcluded.reduce((s, v) => s + v.conv, 0), [purchaseExcluded]);
  const purchaseTotal = useMemo(() => purchaseVendors.reduce((s, v) => s + v.conv, 0), [purchaseVendors]);
  const piMonthlyTotals = useMemo(() => {
    const m = {}; (piRecords || []).forEach((r) => { const k = r.date.slice(0, 7); m[k] = (m[k] || 0) + r.amount; });
    return Object.entries(m).sort((a, b) => (a[0] < b[0] ? -1 : 1));
  }, [piRecords]);
  const piMonthlyConfig = useMemo(() => ({
    type: "bar",
    data: { labels: piMonthlyTotals.map(([m]) => m), datasets: [{ data: piMonthlyTotals.map(([, v]) => v), backgroundColor: "#9a3412", borderRadius: 5, maxBarThickness: 48 }] },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => " " + won(c.raw) } } }, scales: { y: { ticks: { callback: (v) => won(v) } } } },
  }), [piMonthlyTotals]);
  const piTopVendorConfig = useMemo(() => {
    const top = piVendors.slice(0, 10);
    return {
      type: "bar",
      data: { labels: top.map((v) => (v.vendor.length > 12 ? v.vendor.slice(0, 12) + "…" : v.vendor)), datasets: [{ data: top.map((v) => v.total), backgroundColor: top.map((_, i) => (i < 3 ? "#c2410c" : "#cbd5e1")), borderRadius: 4 }] },
      options: { indexAxis: "y", responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => " " + won(c.raw) + ` (${pct(c.raw, piTotal)})` } } }, scales: { x: { ticks: { callback: (v) => won(v), maxTicksLimit: 6 } } } },
    };
  }, [piVendors, piTotal]);

  const onUploadPiFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = ""; if (!file) return;
    setPiUploading(true); setPiUploadMsg(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSXStyle.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSXStyle.utils.sheet_to_json(ws, { header: 1, defval: "", raw: true });
      const { error, records } = parsePurchaseExcelRows(rows);
      if (error) { setPiUploadMsg({ ok: false, text: error }); return; }
      const r = await savePurchaseRecords(records);
      setPiUploadMsg({ ok: true, text: `${r.from} ~ ${r.to} · 거래처 ${r.vendors}개사 · ${r.saved}건 저장 완료` });
      await loadPi(piDays);
    } catch (err) { setPiUploadMsg({ ok: false, text: err.message || String(err) }); }
    finally { setPiUploading(false); }
  };

  const downloadPurchaseExcel = () => {
    const rows = purchaseVendors.map((v) => ({ "거래처": v.vendor, "건수": v.count, "매입액(원)": Math.round(v.conv), "비중(%)": purchaseTotal > 0 ? Number(((v.conv / purchaseTotal) * 100).toFixed(1)) : "", "최근선적일": v.lastDate }));
    rows.push({ "거래처": "합계", "건수": "", "매입액(원)": Math.round(purchaseTotal), "비중(%)": "", "최근선적일": "" });
    const wb = XLSXStyle.utils.book_new();
    const ws = XLSXStyle.utils.json_to_sheet(rows, { header: ["거래처", "건수", "매입액(원)", "비중(%)", "최근선적일"] });
    XLSXStyle.utils.book_append_sheet(wb, ws, "거래처별매입");
    XLSXStyle.writeFile(wb, `거래처별매입_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ===== 재고·원가 (매출원가 = 기초재고 + 당기매입 − 기말재고) =====
  // 기간 필터와 무관하게 전체 기간을 봐야 월별 흐름이 끊기지 않으므로 별도로 전체를 불러온다.
  const [cogsSales, setCogsSales] = useState(null);
  const [cogsPurchase, setCogsPurchase] = useState(null);
  useEffect(() => { fetchSalesRecords(0).then(setCogsSales).catch(() => setCogsSales([])); }, []);
  useEffect(() => { fetchPurchaseRecords(0).then(setCogsPurchase).catch(() => setCogsPurchase([])); }, []);

  // 매입 탭에 표시할 "마지막 업로드" 요약 — 기간 필터와 무관하게 전체 매입 데이터 기준으로 계산한다.
  const piLastUpload = useMemo(() => {
    const rows = cogsPurchase || [];
    if (!rows.length) return null;
    let last = null;
    rows.forEach((r) => { if (r.updatedAt instanceof Date && (!last || r.updatedAt > last)) last = r.updatedAt; });
    const p2 = (n) => String(n).padStart(2, "0");
    const dates = rows.map((r) => r.date).sort();
    return {
      at: last ? `${last.getFullYear()}-${p2(last.getMonth() + 1)}-${p2(last.getDate())} ${p2(last.getHours())}:${p2(last.getMinutes())}` : null,
      from: dates[0],
      to: dates[dates.length - 1],
      vendors: new Set(rows.map((r) => r.vendorName)).size,
      count: rows.length,
    };
  }, [cogsPurchase]);
  const cogs = useMemo(() => buildMonthlyCogs({
    salesRecords: cogsSales || [], purchaseRecords: cogsPurchase || [], valueHistory,
  }), [cogsSales, cogsPurchase, valueHistory]);
  const cogsReady = cogsSales !== null && cogsPurchase !== null;
  const cogsChartConfig = useMemo(() => {
    const rows = cogs.rows.filter((r) => r.hasInv);
    return {
      type: "bar",
      data: {
        labels: rows.map((r) => r.month),
        datasets: [
          { label: "매출액", data: rows.map((r) => r.sales), backgroundColor: "#1e293b", borderRadius: 4, order: 2 },
          { label: "매출원가", data: rows.map((r) => r.cogs), backgroundColor: "#c2410c", borderRadius: 4, order: 2 },
          { label: "기말재고", type: "line", data: rows.map((r) => r.endInv), borderColor: "#4f46e5", backgroundColor: "#4f46e5", tension: 0.3, order: 1, yAxisID: "y" },
        ],
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } }, tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${won(c.raw)}` } } },
        scales: { y: { ticks: { callback: (v) => won(v), maxTicksLimit: 6 } } },
      },
    };
  }, [cogs]);

  const downloadCogsExcel = () => {
    const rows = cogs.rows.map((r) => ({
      "월": r.month,
      "매출액(원)": Math.round(r.sales),
      "기초재고(원)": r.beginInv == null ? "" : Math.round(r.beginInv),
      "당기매입(원)": Math.round(r.purchase),
      "기말재고(원)": r.endInv == null ? "" : Math.round(r.endInv),
      "재고증감(원)": r.invChange == null ? "" : Math.round(r.invChange),
      "매출원가(원)": r.cogs == null ? "" : Math.round(r.cogs),
      "매출총이익(원)": r.grossProfit == null ? "" : Math.round(r.grossProfit),
      "매출총이익률(%)": r.grossMargin == null ? "" : Number(r.grossMargin.toFixed(1)),
      "재고회전(개월)": r.turnoverMonths == null ? "" : Number(r.turnoverMonths.toFixed(1)),
      "비고": r.hasInv ? "" : "재고 스냅샷 부족으로 원가 계산 제외",
    }));
    const header = ["월", "매출액(원)", "기초재고(원)", "당기매입(원)", "기말재고(원)", "재고증감(원)", "매출원가(원)", "매출총이익(원)", "매출총이익률(%)", "재고회전(개월)", "비고"];
    const wb = XLSXStyle.utils.book_new();
    XLSXStyle.utils.book_append_sheet(wb, XLSXStyle.utils.json_to_sheet(rows, { header }), "월별매출원가");
    XLSXStyle.writeFile(wb, `월별매출원가_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // ===== 현금흐름 (관리) =====
  const today = ymd(new Date());
  const monthlyForecast = useMemo(() => monthlyPaymentForecast(plans || []), [plans]);
  const [cfSalesRecords, setCfSalesRecords] = useState(null);
  useEffect(() => { fetchSalesRecords(200).then(setCfSalesRecords).catch(() => setCfSalesRecords([])); }, []);
  // 현금흐름은 이카운트 매출 + 브랜디노 비서 매출(로켓그로스·제트배송)을 모두 반영한다.
  // brandinoRecords의 vendorName도 "쿠팡"을 포함하므로 아래 buildMonthlyCashflow의 쿠팡 지급기준이 그대로 적용된다.
  const cfSalesRecordsAll = useMemo(() => [...(cfSalesRecords || []), ...brandinoRecords], [cfSalesRecords, brandinoRecords]);
  const cashflow = useMemo(() => buildMonthlyCashflow({
    salesRecords: cfSalesRecordsAll, monthlyForecast, purchaseInvoiceRecords: piRecords || [], today, monthsAhead: 6,
    actualBalance: 0, fixedCosts: [], otherInflows,
  }), [cfSalesRecordsAll, monthlyForecast, piRecords, today, otherInflows]);

  const [newInflow, setNewInflow] = useState({ name: "", amount: "", recurring: false, month: today.slice(0, 7) });
  const [cfOpen, setCfOpen] = useState({}); // 현금흐름 표에서 펼쳐본 월

  // 현금흐름 월별 상세 내역 — 위 buildMonthlyCashflow와 "같은 규칙"으로 건별 항목을 다시 만들어
  // 금액을 클릭했을 때 근거를 보여준다(합계는 항상 표의 값과 일치해야 한다).
  const cashflowDetail = useMemo(() => {
    const detail = {}; // month -> { inflows:[{label,amount,note}], outflows:[...] }
    const push = (month, kind, item) => {
      if (!month) return;
      if (!detail[month]) detail[month] = { inflows: [], outflows: [] };
      detail[month][kind].push(item);
    };
    const monthOfDate = (d) => (d || "").slice(0, 7);

    // ① 매출 입금 — 쿠팡 70%@+60일·30%@+90일, 그 외 거래처 +3일. 거래처·월 단위로 합산.
    // (브랜디노 비서 매출도 vendorName에 "쿠팡"이 포함돼 있어 동일한 지급기준으로 잡힌다.)
    const salesAgg = {};
    (cfSalesRecordsAll || []).forEach((r) => {
      const parts = /쿠팡/i.test(r.vendorName || "")
        ? [{ ratio: 0.7, days: 60, tag: "70%·+60일" }, { ratio: 0.3, days: 90, tag: "30%·+90일" }]
        : [{ ratio: 1, days: 3, tag: "+3일" }];
      parts.forEach((p) => {
        const m = monthOfDate(addDays(r.date, p.days));
        const key = `${m}|${r.vendorName}|${p.tag}`;
        if (!salesAgg[key]) salesAgg[key] = { month: m, vendor: r.vendorName, tag: p.tag, amount: 0 };
        salesAgg[key].amount += r.amount * p.ratio;
      });
    });
    Object.values(salesAgg).forEach((a) => push(a.month, "inflows", { label: a.vendor, amount: a.amount, note: `매출입금 ${a.tag}` }));

    // ② 기타 입금(대출·보조금 등)
    (otherInflows || []).forEach((c) => {
      const amt = Number(c.amount) || 0;
      if (!amt) return;
      if (c.recurring) cashflow.base.forEach((r) => push(r.month, "inflows", { label: c.name, amount: amt, note: "기타 입금(매월)" }));
      else push(c.month, "inflows", { label: c.name, amount: amt, note: "기타 입금" });
    });

    // ③ 구매계획 결제(payTerm 해석 결과 — 거래처 덮어쓰기 규칙까지 그대로 반영됨)
    (plans || []).forEach((p) => {
      if (p.done) return;
      paymentSchedule(p).forEach((s) => {
        const krw = isUSD(p.cur) ? s.amount * (Number(p.rate) || 0) : s.amount;
        if (!krw) return;
        const cur = isUSD(p.cur) ? `$${Math.round(s.amount).toLocaleString()} × ${nf(p.rate)}원` : "";
        push(s.month, "outflows", {
          label: p.vendor || "(거래처 미지정)",
          amount: krw,
          note: `구매계획 · ${p.payTerm || "조건없음"} · 선적 ${p.date || "-"}${cur ? " · " + cur : ""}`,
        });
      });
    });

    // ④ 매입세금계산서 실제 매입(매입월 말일+10일, 우진 등은 청구주기 적용) — 거래처·월 단위 합산
    const piAgg = {};
    (piRecords || []).forEach((r) => {
      const [yS, mS, dS] = String(r.date || "").split("-");
      let y = Number(yS), mo = Number(mS);
      const day = Number(dS);
      if (!y || !mo || !day) return;
      if (/우진/.test(r.vendorName || "") && day >= 23) { mo += 1; if (mo > 12) { mo = 1; y += 1; } }
      let ny = y, nm = mo + 1;
      if (nm > 12) { nm = 1; ny += 1; }
      const payMonth = monthOfDate(addDays(addDays(`${ny}-${String(nm).padStart(2, "0")}-01`, -1), 10));
      const key = `${payMonth}|${r.vendorName}`;
      if (!piAgg[key]) piAgg[key] = { month: payMonth, vendor: r.vendorName, amount: 0 };
      piAgg[key].amount += r.amount;
    });
    Object.values(piAgg).forEach((a) => push(a.month, "outflows", { label: a.vendor, amount: a.amount, note: "매입세금계산서 · 매입월 말일+10일" }));

    Object.values(detail).forEach((d) => {
      d.inflows.sort((a, b) => b.amount - a.amount);
      d.outflows.sort((a, b) => b.amount - a.amount);
    });
    return detail;
  }, [cfSalesRecordsAll, otherInflows, plans, piRecords, cashflow]);

  const cashflowChartConfig = useMemo(() => ({
    type: "line",
    data: {
      labels: cashflow.base.map((m) => m.month),
      datasets: [
        { label: "누적 현금흐름(기본)", data: cashflow.base.map((m) => m.cumulative), borderColor: "#0F5C6B", backgroundColor: "rgba(15,92,107,0.08)", fill: true, tension: 0.25 },
        { label: "누적 현금흐름(보수적, +2주 지연)", data: cashflow.conservative.map((m) => m.cumulative), borderColor: "#B3261E", borderDash: [5, 4], fill: false, tension: 0.25 },
      ],
    },
    options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 11 } } }, tooltip: { callbacks: { label: (c) => ` ${c.dataset.label}: ${won(c.raw)}` } } }, scales: { y: { ticks: { callback: (v) => won(v) } } } },
  }), [cashflow]);

  const firstNegative = cashflow.base.find((m) => m.cumulative < 0);

  return (
    <div className="fixed inset-0 z-[9999] bg-white overflow-auto">
      <div className="max-w-[1440px] mx-auto p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
          <div className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-indigo-600" />
            <h2 className="text-xl font-bold text-slate-800">매출매입</h2>
            <span className="text-xs text-slate-400 ml-1">이카운트 매출 · 구매계획 매입 · 현금흐름을 한 화면에서 관리합니다</span>
          </div>
          <button onClick={onClose} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all">
            <X className="h-4 w-4" />닫기
          </button>
        </div>

        <div className="flex items-center gap-2 mb-5">
          <TabBtn active={innerTab === "sales"} onClick={() => setInnerTab("sales")} icon={<TrendingUp className="h-4 w-4" />}>매출</TabBtn>
          <TabBtn active={innerTab === "purchase"} onClick={() => setInnerTab("purchase")} icon={<TrendingDown className="h-4 w-4" />}>매입</TabBtn>
          <TabBtn active={innerTab === "cogs"} onClick={() => setInnerTab("cogs")} icon={<Boxes className="h-4 w-4" />}>재고·원가</TabBtn>
          <TabBtn active={innerTab === "cashflow"} onClick={() => setInnerTab("cashflow")} icon={<Wallet className="h-4 w-4" />}>현금흐름</TabBtn>
        </div>

        {/* ===================== 매출 탭 ===================== */}
        {innerTab === "sales" && (
          <>
            <div className="flex items-center justify-end gap-2 mb-4 flex-wrap gap-y-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onUploadFile} className="hidden" />
              {/* 업로드 형식마다 부가세 처리가 다르므로 기준을 화면에 명시 */}
              <span className="mr-auto text-xs text-slate-500">
                매출 금액은 <b className="text-slate-700">부가세 포함</b> 기준입니다 · 이카운트 판매현황은 <b className="text-slate-700">업로드 금액 그대로</b>, 홈택스 세금계산서는 공급가액 × 1.1 · 원가·마진 계산은 공급가액(÷1.1) 사용
              </span>
              <label className="flex items-center gap-1 text-xs text-slate-500 select-none">
                <input type="checkbox" checked={replaceMode} onChange={(e) => setReplaceMode(e.target.checked)} />
                전체 교체(기존 매출 자료 삭제 후 이 파일로 대체)
              </label>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className={"flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 " + (replaceMode ? "bg-red-600 hover:bg-red-700" : "bg-slate-900 hover:bg-slate-800")}>
                <Upload className="h-4 w-4" />{uploading ? "처리 중…" : replaceMode ? "판매현황 엑셀 업로드(전체 교체)" : "판매현황 엑셀 업로드"}
              </button>
              <div className="flex flex-wrap bg-slate-100 border border-slate-200/60 rounded-lg p-0.5 gap-0.5">
                {SALES_RANGES.map((r) => (
                  <button key={r.d} onClick={() => setDays(r.d)} className={"px-2.5 py-1 rounded-md text-xs font-medium transition-all " + (days === r.d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800")}>{r.t}</button>
                ))}
              </div>
              <button onClick={() => load(days)} disabled={loading} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50">
                <RefreshCw className={"h-4 w-4" + (loading ? " animate-spin" : "")} />새로고침
              </button>
            </div>

            {uploadMsg && <p className={"text-sm mb-3 " + (uploadMsg.ok ? "text-emerald-600" : "text-red-600")}>{uploadMsg.ok ? "✓ " : "업로드 오류: "}{uploadMsg.text}</p>}
            {err && <p className="text-red-600 text-sm mb-3">불러오기 오류: {err}</p>}
            {records === null && <p className="text-slate-500 text-sm py-10 text-center">불러오는 중…</p>}

            {/* 브랜디노 비서 매출 — 쿠팡 로켓그로스·제트배송(이카운트 판매현황 엑셀에는 안 잡히는 채널).
                전부 자동 조회이고, 남아있는 과거 수동 보정값만 "✎ 보정" 표시와 함께 삭제할 수 있게 둔다. */}
            <div className="flex items-center gap-2 mb-2 text-xs">
              {brandinoApiLoading && <span className="text-slate-400">브랜디노 비서 자동 조회 중…</span>}
              {!brandinoApiLoading && brandinoApiErr && <span className="text-red-600">브랜디노 비서 자동 조회 실패({brandinoApiErr})</span>}
              {!brandinoApiLoading && !brandinoApiErr && <span className="text-emerald-600">✓ 브랜디노 비서 자동 조회 완료({nf((brandinoApi || []).length)}건) · 브랜디노 비서를 마지막으로 배포한 시점 기준입니다</span>}
              <button onClick={loadBrandinoApi} disabled={brandinoApiLoading} className="ml-auto flex items-center gap-1 text-slate-400 hover:text-slate-700 disabled:opacity-50">
                <RefreshCw className={"h-3 w-3" + (brandinoApiLoading ? " animate-spin" : "")} />다시 조회
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
              {BRANDINO_CHANNELS.map((ch) => {
                const manualDates = new Set((brandinoSales || []).filter((b) => b.channel === ch.key).map((b) => b.date));
                const rows = brandinoRecords.filter((r) => r.vendorName === ch.label).sort((a, b) => (a.date < b.date ? 1 : -1));
                const chTotal = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                return (
                  <div key={ch.key} className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-bold text-slate-700">{ch.label} <span className="text-xs font-normal text-slate-400">(브랜디노 비서)</span></div>
                      <div className="text-sm font-semibold text-indigo-700">{won(chTotal)}</div>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {rows.length === 0 && <p className="text-xs text-slate-400">아직 매출 데이터가 없습니다.</p>}
                      {rows.map((r) => {
                        const isManual = manualDates.has(r.date);
                        return (
                          <div key={r.date} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-1.5 text-xs">
                            <span className="text-slate-500">{r.date}{isManual && <span className="ml-1 text-amber-500" title="예전에 수동 입력해둔 보정값(자동 조회값보다 우선 적용됨)">✎ 보정</span>}</span>
                            <span className="font-medium">{won(r.amount)}</span>
                            {isAdmin && isManual && <button onClick={() => removeBrandinoSale?.(`${ch.key}_${r.date}`)} title="보정값 삭제(자동 조회값으로 복원)" className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            {((records && records.length > 0) || brandinoTotal > 0) && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                  <SummaryCard label="엑셀 업로드 매출" value={won(excelTotal)} sub="이카운트 판매현황 업로드" />
                  <SummaryCard label="브랜디노 비서 매출" value={won(brandinoTotal)} sub="쿠팡 로켓그로스+제트배송" />
                  <SummaryCard label="합계(총매출)" value={won(total)} sub={SALES_RANGES.find((r) => r.d === days)?.t} tone="amber" />
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
                  <SummaryCard label="전월 대비" value={mom ? `${mom.pct >= 0 ? "▲" : "▼"} ${Math.abs(mom.pct).toFixed(1)}%` : "—"} sub={mom ? `${mom.month} vs ${mom.prev}` : "비교할 전월 데이터 없음"} />
                  <SummaryCard label="거래 업체 수" value={`${nf(vendors.length)}개사`} sub={`일평균 ${won(dateCount > 0 ? total / dateCount : 0)}`} />
                  <SummaryCard label="최대 매출 업체" value={vendors[0] ? vendors[0].vendor : "—"} sub={vendors[0] ? `${won(vendors[0].total)} · ${pct(vendors[0].total, total)}` : ""} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"><div className="text-sm font-bold text-slate-700 mb-2">월별 매출</div><ChartBox config={monthlyConfig} height={220} /></div>
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"><div className="text-sm font-bold text-slate-700 mb-2">업체별 매출 TOP 10</div><ChartBox config={topVendorConfig} height={220} /></div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-100 text-slate-600">
                      <th className="w-8 px-2 py-2"></th><th className="text-left font-semibold px-3 py-2">순위</th><th className="text-left font-semibold px-3 py-2">업체명</th>
                      <th className="text-right font-semibold px-3 py-2">거래일수</th><th className="text-right font-semibold px-3 py-2">매출액</th><th className="text-right font-semibold px-3 py-2">비중</th><th className="text-right font-semibold px-3 py-2">최근거래일</th>
                    </tr></thead>
                    <tbody>
                      {vendors.map((v, i) => {
                        const key = v.code || v.vendor;
                        const isOpen = !!open[key];
                        const dailyRows = Object.entries(v.daily).sort((a, b) => (a[0] < b[0] ? 1 : -1));
                        return (
                          <React.Fragment key={key}>
                            <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setOpen((o) => ({ ...o, [key]: !o[key] }))}>
                              <td className="px-2 py-2 text-slate-400">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                              <td className="px-3 py-2 text-slate-400">{i + 1}</td><td className="px-3 py-2 font-medium">{v.vendor}</td>
                              <td className="px-3 py-2 text-right">{nf(v.dayCount)}</td><td className="px-3 py-2 text-right font-semibold">{won(v.total)}</td>
                              <td className="px-3 py-2 text-right text-slate-500">{pct(v.total, total)}</td><td className="px-3 py-2 text-right text-slate-500">{v.lastDate}</td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-slate-50/60"><td></td><td colSpan={6} className="px-3 pb-3 pt-1">
                                <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white max-w-md">
                                  <table className="w-full text-xs"><thead><tr className="bg-slate-100 text-slate-500"><th className="text-left px-2 py-1.5">판매일</th><th className="text-right px-2 py-1.5">매출액</th></tr></thead>
                                    <tbody>{dailyRows.map(([d, amt]) => <tr key={d} className="border-t border-slate-100"><td className="px-2 py-1.5">{d}</td><td className="px-2 py-1.5 text-right">{won(amt)}</td></tr>)}</tbody>
                                  </table>
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </>
        )}

        {/* ===================== 매입 탭 ===================== */}
        {innerTab === "purchase" && (
          <>
            {/* --- 실제 매입 (매입세금계산서 업로드) --- */}
            <div className="flex items-center justify-between mb-3 flex-wrap gap-y-2">
              <div className="text-sm font-bold text-slate-700">실제 매입 <span className="font-normal text-slate-400 text-xs ml-1">매입세금계산서 기준</span></div>
              <div className="flex items-center gap-2 flex-wrap gap-y-2">
                <input ref={piFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={onUploadPiFile} className="hidden" />
                <button onClick={() => piFileRef.current?.click()} disabled={piUploading} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-900 text-white hover:bg-slate-800 transition-all disabled:opacity-50">
                  <Upload className="h-4 w-4" />{piUploading ? "처리 중…" : "매입세금계산서 업로드"}
                </button>
                <div className="flex bg-slate-100 border border-slate-200/60 rounded-lg p-0.5 gap-0.5">
                  {RANGES.map((r) => (
                    <button key={r.d} onClick={() => setPiDays(r.d)} className={"px-2.5 py-1 rounded-md text-xs font-medium transition-all " + (piDays === r.d ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800")}>{r.t}</button>
                  ))}
                </div>
                <button onClick={() => loadPi(piDays)} disabled={piLoading} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50">
                  <RefreshCw className={"h-4 w-4" + (piLoading ? " animate-spin" : "")} />새로고침
                </button>
              </div>
            </div>
            <p className="text-[11px] text-slate-400 mb-3">홈택스 전자세금계산서 목록 엑셀을 그대로 업로드하면 구분="매입" 행만 자동으로 추출해 반영합니다(구분="매출" 행은 매출 탭 업로드용).</p>

            {piUploadMsg && <p className={"text-sm mb-3 " + (piUploadMsg.ok ? "text-emerald-600" : "text-red-600")}>{piUploadMsg.ok ? "✓ " : "업로드 오류: "}{piUploadMsg.text}</p>}
            {!piUploadMsg && piLastUpload && (
              <p className="text-sm mb-3 text-emerald-600">
                ✓ 마지막 업로드{piLastUpload.at ? ` ${piLastUpload.at}` : ""} · {piLastUpload.from} ~ {piLastUpload.to} · 거래처 {nf(piLastUpload.vendors)}개사 · {nf(piLastUpload.count)}건 저장됨
              </p>
            )}
            {piErr && <p className="text-red-600 text-sm mb-3">불러오기 오류: {piErr}</p>}
            {piRecords === null && <p className="text-slate-500 text-sm py-6 text-center">불러오는 중…</p>}

            {piRecords && piRecords.length > 0 && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  <SummaryCard label="기간 실제 매입액" value={won(piTotal)} sub={RANGES.find((r) => r.d === piDays)?.t} tone="amber" />
                  <SummaryCard label="거래처 수" value={`${nf(piVendors.length)}개사`} />
                  <SummaryCard label="최대 매입 거래처" value={piVendors[0] ? piVendors[0].vendor : "—"} sub={piVendors[0] ? `${won(piVendors[0].total)} · ${pct(piVendors[0].total, piTotal)}` : ""} />
                  <SummaryCard label="최근 계산서 발행일" value={piVendors[0]?.lastDate || "—"} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"><div className="text-sm font-bold text-slate-700 mb-2">월별 매입</div><ChartBox config={piMonthlyConfig} height={200} /></div>
                  <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4"><div className="text-sm font-bold text-slate-700 mb-2">거래처별 매입 TOP 10</div><ChartBox config={piTopVendorConfig} height={200} /></div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-100 text-slate-600">
                      <th className="w-8 px-2 py-2"></th><th className="text-left font-semibold px-3 py-2">거래처</th><th className="text-right font-semibold px-3 py-2">계산서 발행일수</th>
                      <th className="text-right font-semibold px-3 py-2">매입액</th><th className="text-right font-semibold px-3 py-2">비중</th><th className="text-right font-semibold px-3 py-2">최근발행일</th>
                    </tr></thead>
                    <tbody>
                      {piVendors.map((v) => {
                        const key = v.code || v.vendor;
                        const isOpen = !!piOpen[key];
                        const dailyRows = Object.entries(v.daily).sort((a, b) => (a[0] < b[0] ? 1 : -1));
                        return (
                          <React.Fragment key={key}>
                            <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setPiOpen((o) => ({ ...o, [key]: !o[key] }))}>
                              <td className="px-2 py-2 text-slate-400">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                              <td className="px-3 py-2 font-medium">{v.vendor}</td><td className="px-3 py-2 text-right">{nf(v.dayCount)}</td>
                              <td className="px-3 py-2 text-right font-semibold">{won(v.total)}</td><td className="px-3 py-2 text-right text-slate-500">{pct(v.total, piTotal)}</td>
                              <td className="px-3 py-2 text-right text-slate-500">{v.lastDate}</td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-slate-50/60"><td></td><td colSpan={5} className="px-3 pb-3 pt-1">
                                <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white max-w-md">
                                  <table className="w-full text-xs"><thead><tr className="bg-slate-100 text-slate-500"><th className="text-left px-2 py-1.5">발행일</th><th className="text-right px-2 py-1.5">매입액</th></tr></thead>
                                    <tbody>{dailyRows.map(([d, amt]) => <tr key={d} className="border-t border-slate-100"><td className="px-2 py-1.5">{d}</td><td className="px-2 py-1.5 text-right">{won(amt)}</td></tr>)}</tbody>
                                  </table>
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {piRecords && piRecords.length === 0 && !piErr && (
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm mb-8">업로드된 매입세금계산서가 없습니다. 위 버튼으로 엑셀을 업로드해주세요.</div>
            )}

            {/* --- 구매계획 기준 매입 (발주앱, 예정 금액) --- */}
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-bold text-slate-700">구매계획 기준 매입 <span className="font-normal text-slate-400 text-xs ml-1">발주 계획 앱 예정 금액</span></div>
              <div className="flex items-center gap-2">
                <button onClick={downloadPurchaseExcel} disabled={!purchaseVendors.length} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50"><FileSpreadsheet className="h-4 w-4" />엑셀</button>
                <button onClick={loadPlans} disabled={plansLoading} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition-all disabled:opacity-50"><RefreshCw className={"h-4 w-4" + (plansLoading ? " animate-spin" : "")} />새로고침</button>
              </div>
            </div>
            {plansErr && <p className="text-red-600 text-sm mb-3">불러오기 오류: {plansErr}</p>}
            {purchaseExcluded.length > 0 && (
              <p className="text-[11px] text-amber-600 mb-3">
                상단 실제 매입(세금계산서)과 중복되는 거래처 {nf(purchaseExcluded.length)}개사 · {won(purchaseExcludedTotal)}는 제외하고 집계했습니다 — {purchaseExcluded.map((v) => v.vendor).join(", ")}
              </p>
            )}
            {plans === null && <p className="text-slate-500 text-sm py-10 text-center">불러오는 중…</p>}
            {plans && purchaseVendors.length > 0 && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  <SummaryCard label="전체 매입액(KRW 환산)" value={won(purchaseTotal)} sub={`발주 ${nf(purchaseVendors.reduce((s, v) => s + v.count, 0))}건${purchaseExcluded.length ? ` · 중복 ${nf(purchaseExcluded.length)}개사 제외` : ""}`} />
                  <SummaryCard label="거래처 수" value={`${nf(purchaseVendors.length)}개사`} />
                  <SummaryCard label="최대 매입 거래처" value={purchaseVendors[0] ? purchaseVendors[0].vendor : "—"} sub={purchaseVendors[0] ? won(purchaseVendors[0].conv) : ""} />
                  <SummaryCard label="최근 발주일" value={purchaseVendors[0]?.lastDate || "—"} />
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-slate-100 text-slate-600">
                      <th className="w-8 px-2 py-2"></th><th className="text-left font-semibold px-3 py-2">거래처</th><th className="text-right font-semibold px-3 py-2">건수</th>
                      <th className="text-right font-semibold px-3 py-2">매입액(KRW환산)</th><th className="text-right font-semibold px-3 py-2">비중</th><th className="text-right font-semibold px-3 py-2">최근선적일</th>
                    </tr></thead>
                    <tbody>
                      {purchaseVendors.map((v) => {
                        const isOpen = !!purchaseOpen[v.vendor];
                        return (
                          <React.Fragment key={v.vendor}>
                            <tr className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setPurchaseOpen((o) => ({ ...o, [v.vendor]: !o[v.vendor] }))}>
                              <td className="px-2 py-2 text-slate-400">{isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</td>
                              <td className="px-3 py-2 font-medium">{v.vendor}</td><td className="px-3 py-2 text-right">{nf(v.count)}</td>
                              <td className="px-3 py-2 text-right font-semibold">{won(v.conv)}</td><td className="px-3 py-2 text-right text-slate-500">{pct(v.conv, purchaseTotal)}</td>
                              <td className="px-3 py-2 text-right text-slate-500">{v.lastDate}</td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-slate-50/60"><td></td><td colSpan={5} className="px-3 pb-3 pt-1">
                                <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white max-w-lg">
                                  <table className="w-full text-xs"><thead><tr className="bg-slate-100 text-slate-500">
                                    <th className="text-left px-2 py-1.5">선적일</th><th className="text-right px-2 py-1.5">금액</th><th className="text-left px-2 py-1.5">결제조건</th><th className="text-center px-2 py-1.5">완료</th>
                                  </tr></thead>
                                    <tbody>{v.items.sort((a, b) => (b.date || "").localeCompare(a.date || "")).map((it, i) => (
                                      <tr key={i} className="border-t border-slate-100">
                                        <td className="px-2 py-1.5">{it.date || "-"}</td>
                                        <td className="px-2 py-1.5 text-right">{isUSD(it.cur) ? "$" + nf(it.totAmt) : won(it.totAmt)}</td>
                                        <td className="px-2 py-1.5">{it.payTerm || "-"}</td>
                                        <td className="px-2 py-1.5 text-center">{it.done ? "✓" : ""}</td>
                                      </tr>
                                    ))}</tbody>
                                  </table>
                                </div>
                              </td></tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {plans && purchaseVendors.length === 0 && !plansErr && <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-slate-500">{purchaseVendorsAll.length > 0 ? "구매계획 거래처가 모두 상단 실제 매입에 포함되어 있어 표시할 항목이 없습니다." : "구매계획 이력이 없습니다."}</div>}
          </>
        )}

        {/* ===================== 재고·원가 탭 ===================== */}
        {innerTab === "cogs" && (
          <>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-y-2">
              <p className="text-xs text-slate-500">
                매출원가 = <span className="font-semibold text-slate-700">기초재고 + 당기매입 − 기말재고</span> · 매입은 비용이 아니라 재고자산 취득이고, 팔릴 때 비로소 원가가 됩니다.
              </p>
              <button onClick={downloadCogsExcel} disabled={!cogsReady || cogs.rows.length === 0} className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-all disabled:opacity-40">
                <FileSpreadsheet className="h-4 w-4" />엑셀 다운로드
              </button>
            </div>

            {!cogsReady && <p className="text-slate-500 text-sm py-10 text-center">불러오는 중…</p>}

            {cogsReady && cogs.totals.months === 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 text-center text-sm text-amber-800">
                매출원가를 계산하려면 <span className="font-semibold">연속된 두 달 이상의 월말 재고 스냅샷</span>이 필요합니다.<br />
                <span className="text-xs text-amber-700">재고금액 화면의 '오늘 재고 기록'을 매월 말일에 남겨 주시면 그 다음 달부터 자동으로 계산됩니다.</span>
              </div>
            )}

            {cogsReady && cogs.totals.months > 0 && (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                  <SummaryCard label={`누적 매출액 (${cogs.totals.months}개월)`} value={won(cogs.totals.sales)} sub="공급가액 기준" />
                  <SummaryCard label="누적 매출원가" value={won(cogs.totals.cogs)} sub={`누적 매입 ${won(cogs.totals.purchase)}`} tone="amber" />
                  <SummaryCard label="누적 매출총이익" value={won(cogs.totals.grossProfit)} sub={cogs.totals.grossMargin == null ? "" : `이익률 ${cogs.totals.grossMargin.toFixed(1)}%`} />
                  <SummaryCard
                    label="재고자산 증감"
                    value={(cogs.totals.invChange > 0 ? "+" : "") + won(cogs.totals.invChange)}
                    sub={`${won(cogs.totals.firstInv)} → ${won(cogs.totals.lastInv)}`}
                    tone={cogs.totals.invChange > 0 ? "red" : undefined}
                  />
                </div>

                {cogs.totals.invChange > 0 && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 mb-4 text-xs text-red-800">
                    이 기간 재고자산이 <span className="font-bold">{won(cogs.totals.invChange)}</span> 늘었습니다. 그만큼의 현금이 이미 나갔지만 아직 비용(매출원가)이 되지 않고 상품 형태로 묶여 있다는 뜻입니다 — 장부상 이익과 통장 잔액이 벌어지는 주된 원인입니다.
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
                  <div className="text-sm font-bold text-slate-700 mb-2">월별 매출액 · 매출원가 · 기말재고</div>
                  <ChartBox config={cogsChartConfig} height={260} />
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="h-9">
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-left bg-slate-800">월</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-slate-800">매출액</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-indigo-800">기초재고</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-orange-800">＋당기매입</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-indigo-800">−기말재고</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-slate-600">재고증감</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-red-800">＝매출원가</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-emerald-800">매출총이익</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-emerald-800">이익률</th>
                        <th className="whitespace-nowrap px-2.5 py-2 text-[11px] font-bold text-white text-right bg-slate-600">재고회전</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cogs.rows.map((r) => (
                        <tr key={r.month} className={"h-9 border-b border-slate-100 " + (r.hasInv ? "hover:bg-slate-50" : "bg-slate-50/60 text-slate-400")}>
                          <td className="px-2.5 py-1.5 font-semibold text-slate-700 whitespace-nowrap">
                            {r.month}
                            {r.endInvDate && <span className="ml-1 text-[10px] font-normal text-slate-400">({r.endInvDate.slice(5)} 기록)</span>}
                          </td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap">{won(r.sales)}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap text-slate-500">{r.beginInv == null ? "—" : won(r.beginInv)}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap text-orange-700">{won(r.purchase)}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap text-slate-500">{r.endInv == null ? "—" : won(r.endInv)}</td>
                          <td className={"px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap font-medium " + (r.invChange == null ? "" : r.invChange > 0 ? "text-red-600" : "text-emerald-600")}>
                            {r.invChange == null ? "—" : (r.invChange > 0 ? "+" : "") + won(r.invChange)}
                          </td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap font-semibold text-red-700">{r.cogs == null ? "—" : won(r.cogs)}</td>
                          <td className={"px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap font-semibold " + (r.grossProfit == null ? "" : r.grossProfit >= 0 ? "text-emerald-700" : "text-red-700")}>
                            {r.grossProfit == null ? "—" : won(r.grossProfit)}
                          </td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap">{r.grossMargin == null ? "—" : r.grossMargin.toFixed(1) + "%"}</td>
                          <td className="px-2.5 py-1.5 text-right tabular-nums whitespace-nowrap text-slate-500">{r.turnoverMonths == null ? "—" : r.turnoverMonths.toFixed(1) + "개월"}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 bg-slate-50 h-10">
                        <td className="px-2.5 py-2 font-bold text-slate-700">합계</td>
                        <td className="px-2.5 py-2 text-right font-bold tabular-nums">{won(cogs.totals.sales)}</td>
                        <td className="px-2.5 py-2 text-right tabular-nums text-slate-400">{won(cogs.totals.firstInv)}</td>
                        <td className="px-2.5 py-2 text-right font-bold tabular-nums text-orange-700">{won(cogs.totals.purchase)}</td>
                        <td className="px-2.5 py-2 text-right tabular-nums text-slate-400">{won(cogs.totals.lastInv)}</td>
                        <td className={"px-2.5 py-2 text-right font-bold tabular-nums " + (cogs.totals.invChange > 0 ? "text-red-600" : "text-emerald-600")}>
                          {(cogs.totals.invChange > 0 ? "+" : "") + won(cogs.totals.invChange)}
                        </td>
                        <td className="px-2.5 py-2 text-right font-bold tabular-nums text-red-700">{won(cogs.totals.cogs)}</td>
                        <td className="px-2.5 py-2 text-right font-bold tabular-nums text-emerald-700">{won(cogs.totals.grossProfit)}</td>
                        <td className="px-2.5 py-2 text-right font-bold tabular-nums">{cogs.totals.grossMargin == null ? "—" : cogs.totals.grossMargin.toFixed(1) + "%"}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <p className="text-[11px] text-slate-400 mt-4 leading-relaxed">
                  · 매출액·매입액·재고금액 모두 <b>공급가액(부가세 제외)</b> 기준이라 그대로 더하고 뺄 수 있습니다.<br />
                  · 기말재고는 그 달에 기록된 <b>마지막 재고 스냅샷</b>, 기초재고는 <b>직전 달의 기말재고</b>입니다. 둘 중 하나라도 없는 달은 추정하지 않고 원가를 비워 둡니다(회색 행).<br />
                  · 매입은 <b>매입세금계산서(실제 발생액)</b> 기준입니다. 구매계획(발주 예정)은 아직 매입이 아니므로 제외됩니다.<br />
                  · 재고회전(개월) = 기말재고 ÷ 그 달 매출원가 — 지금 재고가 몇 달치인지를 뜻하며, 길수록 자금이 오래 묶여 있다는 신호입니다.<br />
                  · 월말이 아닌 날짜의 스냅샷이 그 달의 마지막 기록이면 원가가 그만큼 왜곡될 수 있으니, 매월 말일 기록을 권장드립니다.
                </p>
              </>
            )}
          </>
        )}

        {/* ===================== 현금흐름 탭 ===================== */}
        {innerTab === "cashflow" && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              <SummaryCard label={cashflow.base[0]?.month + " 말 예상 누적현금"} value={won(cashflow.base[0]?.cumulative)} sub="기초 잔액 0원 기준 · 순증감 누적" />
              <SummaryCard label="현금흐름 음수 전환월" value={firstNegative ? firstNegative.month : "없음"} tone={firstNegative ? "red" : undefined} />
              <SummaryCard label="등록된 기타 입금" value={`${nf(otherInflows.length)}건`} sub={won(otherInflows.reduce((s, c) => s + (Number(c.amount) || 0), 0))} />
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4 mb-4">
              <div className="text-sm font-bold text-slate-700 mb-2">누적 현금흐름 전망 (향후 {cashflow.base.length}개월) <span className="font-normal text-slate-400 text-xs ml-1">아래 표에서 월을 클릭하면 상세 내역이 열립니다</span></div>
              <ChartBox config={cashflowChartConfig} height={240} />
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-x-auto mb-6">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800 text-white">
                  <th className="text-left px-3 py-2">월</th><th className="text-right px-3 py-2">현금유입(+)</th><th className="text-right px-3 py-2">매입지급(-)</th>
                  <th className="text-right px-3 py-2">(내역)</th><th className="text-right px-3 py-2">순현금흐름</th><th className="text-right px-3 py-2">누적(기본)</th><th className="text-right px-3 py-2">누적(보수적)</th>
                </tr></thead>
                <tbody>
                  {cashflow.base.map((m, i) => {
                    const d = cashflowDetail[m.month] || { inflows: [], outflows: [] };
                    const isOpen = !!cfOpen[m.month];
                    const hasDetail = d.inflows.length > 0 || d.outflows.length > 0;
                    return (
                      <React.Fragment key={m.month}>
                        <tr className={"border-t border-slate-100 " + (hasDetail ? "hover:bg-slate-50 cursor-pointer" : "")}
                          onClick={() => hasDetail && setCfOpen((o) => ({ ...o, [m.month]: !o[m.month] }))}>
                          <td className="px-3 py-2 font-medium">
                            <span className="inline-flex items-center gap-1">
                              {hasDetail && (isOpen ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />)}
                              {m.month}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-emerald-600">
                            {won(m.inflow)}
                            {m.otherInflow > 0 && <div className="text-[10px] text-slate-400 font-normal">매출 {won(m.salesInflow)} · 기타 {won(m.otherInflow)}</div>}
                          </td>
                          <td className="px-3 py-2 text-right text-red-600">{won(m.outflow)}</td>
                          <td className="px-3 py-2 text-right text-slate-400 text-xs">구매계획 {won(m.planOutflow)} · 실제매입 {won(m.invoiceOutflow)}</td>
                          <td className="px-3 py-2 text-right font-semibold">{won(m.net)}</td>
                          <td className={"px-3 py-2 text-right font-semibold " + (m.cumulative < 0 ? "text-red-600" : "")}>{won(m.cumulative)}</td>
                          <td className="px-3 py-2 text-right text-slate-500">{won(cashflow.conservative[i]?.cumulative)}</td>
                        </tr>
                        {isOpen && (
                          <tr className="bg-slate-50/60">
                            <td colSpan={7} className="px-4 pb-4 pt-2">
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                                <div className="rounded-xl border border-emerald-200 bg-white overflow-hidden">
                                  <div className="px-3 py-1.5 bg-emerald-50 text-emerald-800 text-xs font-bold flex justify-between">
                                    <span>{m.month} 유입 상세</span><span>{won(m.inflow)}</span>
                                  </div>
                                  <table className="w-full text-xs">
                                    <tbody>
                                      {d.inflows.length === 0 && <tr><td className="px-3 py-2 text-slate-400">유입 내역이 없습니다.</td></tr>}
                                      {d.inflows.map((x, k) => (
                                        <tr key={k} className="border-t border-slate-100">
                                          <td className="px-3 py-1.5">{x.label}<div className="text-[10px] text-slate-400">{x.note}</div></td>
                                          <td className="px-3 py-1.5 text-right whitespace-nowrap text-emerald-700 font-medium">{won(x.amount)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="rounded-xl border border-red-200 bg-white overflow-hidden">
                                  <div className="px-3 py-1.5 bg-red-50 text-red-800 text-xs font-bold flex justify-between">
                                    <span>{m.month} 출금 상세</span><span>{won(m.outflow)}</span>
                                  </div>
                                  <table className="w-full text-xs">
                                    <tbody>
                                      {d.outflows.length === 0 && <tr><td className="px-3 py-2 text-slate-400">출금 내역이 없습니다.</td></tr>}
                                      {d.outflows.map((x, k) => (
                                        <tr key={k} className="border-t border-slate-100">
                                          <td className="px-3 py-1.5">{x.label}<div className="text-[10px] text-slate-400">{x.note}</div></td>
                                          <td className="px-3 py-1.5 text-right whitespace-nowrap text-red-700 font-medium">{won(x.amount)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 기타 입금 목록은 모든 사용자에게 표시하고, 등록·삭제만 관리자로 제한 */}
            <div className="grid grid-cols-1 gap-4">
                <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-4">
                  <div className="text-sm font-bold text-slate-700 mb-3">기타 입금 관리 (정부지원 대출·보조금·투자금 등)</div>
                  <div className="space-y-1.5 mb-3 max-h-40 overflow-y-auto">
                    {otherInflows.length === 0 && <p className="text-xs text-slate-400">등록된 기타 입금이 없습니다.</p>}
                    {otherInflows.map((c) => (
                      <div key={c.id} className="flex items-center justify-between gap-2 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs">
                        <span className="font-medium">{c.name}</span>
                        <span className="text-emerald-700">+{won(c.amount)} · {c.recurring ? "매월" : c.month}</span>
                        {isAdmin && <button onClick={() => removeOtherInflow(c.id)} className="text-red-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>}
                      </div>
                    ))}
                  </div>
                  {isAdmin ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <input type="text" placeholder="항목명(예: 정부지원 대출)" value={newInflow.name} onChange={(e) => setNewInflow((c) => ({ ...c, name: e.target.value }))} className="w-40 rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />
                    <input type="number" placeholder="금액" value={newInflow.amount} onChange={(e) => setNewInflow((c) => ({ ...c, amount: e.target.value }))} className="w-32 rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-right" />
                    <label className="flex items-center gap-1 text-xs text-slate-500"><input type="checkbox" checked={newInflow.recurring} onChange={(e) => setNewInflow((c) => ({ ...c, recurring: e.target.checked }))} />매월반복</label>
                    {!newInflow.recurring && <input type="month" value={newInflow.month} onChange={(e) => setNewInflow((c) => ({ ...c, month: e.target.value }))} className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs" />}
                    <button onClick={() => { if (!newInflow.name || !Number(newInflow.amount)) return; addOtherInflow(newInflow.name, newInflow.amount, newInflow.recurring, newInflow.month); setNewInflow({ name: "", amount: "", recurring: false, month: today.slice(0, 7) }); }}
                      className="flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-2.5 py-1.5 text-xs font-medium hover:bg-emerald-700"><Plus className="h-3.5 w-3.5" />추가</button>
                  </div>
                  ) : (
                    <p className="text-xs text-slate-400">기타 입금 등록·삭제는 관리자만 가능합니다.</p>
                  )}
                  {isAdmin && <p className="text-[11px] text-slate-400 mt-2">대출처럼 한 번만 들어오는 항목은 '매월반복'을 끄고 입금될 월을 지정하세요.</p>}
                </div>
              </div>
            <p className="text-[11px] text-slate-300 mt-4">현금유입 = 매출입금(쿠팡 70%@+60일·30%@+90일, 그 외 거래처는 +3일 가정) + 등록된 기타 입금(대출·보조금 등) · 매입지급 = 구매계획 결제조건(payTerm) + 매입세금계산서(매입월 말일+10일, 우진은 전월23일~당월22일 청구주기 적용) · 누적현금은 기초 잔액 0원 기준 순증감 누적 · 보수적 시나리오는 매출입금만 2주 추가 지연 가정(매입지급은 시나리오와 무관하게 고정)</p>
          </>
        )}
      </div>
    </div>
  );
}
